<template>
  <div class="planes-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-tags me-2"></i>
          Gestión de Planes de Suscripción
        </h1>
        <p class="page-subtitle">
          <i class="fas fa-cog me-1"></i>
          Configuración y administración de planes de facturación
        </p>
      </div>
      <button type="button" class="btn-primary-custom" @click="showCreateModal = true">
        <i class="fas fa-plus me-1"></i>
        Nuevo Plan
      </button>
    </div>

    <div class="content-wrapper">
      <!-- Planes activos -->
      <div class="plans-grid">
        <div class="plan-card" v-for="plan in planes" :key="plan.id" :class="{ 'plan-highlighted': plan.destacado }">
          <div class="plan-header" :class="getPlanHeaderClass(plan.nombre)">
            <h3 class="plan-name">{{ plan.nombre }}</h3>
            <div v-if="plan.destacado" class="popular-badge">
              <i class="fas fa-star"></i>
              Más Popular
            </div>
          </div>
          <div class="plan-body">
            <div class="plan-price">
              <div class="price-amount">${{ plan.precio }}</div>
              <div class="price-cycle">/{{ plan.ciclo }}</div>
            </div>
            
            <div class="plan-features">
              <div class="feature-item">
                <i class="fas fa-check feature-check"></i>
                <span>{{ plan.max_usuarios }} usuarios</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check feature-check"></i>
                <span>{{ plan.max_facturas_mes }} facturas/mes</span>
              </div>
              <div class="feature-item" v-if="plan.soporte_prioritario">
                <i class="fas fa-check feature-check"></i>
                <span>Soporte prioritario</span>
              </div>
              <div class="feature-item" v-if="plan.api_access">
                <i class="fas fa-check feature-check"></i>
                <span>Acceso API</span>
              </div>
              <div class="feature-item" v-if="plan.reportes_avanzados">
                <i class="fas fa-check feature-check"></i>
                <span>Reportes avanzados</span>
              </div>
            </div>

            <!-- Módulos incluidos -->
            <div class="plan-modules" v-if="plan.modulos && plan.modulos.length > 0">
              <h5 class="modules-title">
                <i class="fas fa-cubes"></i>
                Módulos Incluidos
              </h5>
              <div class="modules-list">
                <div class="module-item" v-for="modulo in plan.modulos" :key="modulo.id">
                  <i class="fas fa-puzzle-piece"></i>
                  <span>{{ modulo.nombre }}</span>
                </div>
              </div>
            </div>

            <!-- Sección inferior fija -->
            <div class="plan-footer">
              <div class="plan-stats">
                <i class="fas fa-users me-1"></i>
                {{ plan.suscripciones_activas }} suscripciones activas
              </div>

              <div class="plan-actions">
                <button class="action-btn edit-btn" @click="editarPlan(plan)">
                  <i class="fas fa-edit"></i>
                  Editar
                </button>
                <button 
                  class="action-btn delete-btn" 
                  @click="eliminarPlan(plan.id)"
                  :disabled="plan.suscripciones_activas > 0"
                >
                  <i class="fas fa-trash"></i>
                  Eliminar
                </button>
              </div>
              
              <div class="plan-toggle">
                <div class="toggle-switch">
                  <input 
                    class="toggle-input" 
                    type="checkbox" 
                    :id="`activo_${plan.id}`"
                    v-model="plan.activo"
                    @change="togglePlanActivo(plan)"
                  >
                  <label class="toggle-label" :for="`activo_${plan.id}`">
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">{{ plan.activo ? 'Activo' : 'Inactivo' }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Crear/Editar Plan -->
  <div class="modal-overlay" v-if="showCreateModal" @click="closeModal">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">{{ editingPlan ? 'Editar' : 'Crear' }} Plan</h3>
        <button type="button" class="modal-close" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="savePlan" class="plan-form">
          <div class="form-grid">
            <div class="form-section">
              <div class="form-group">
                <label class="form-label">Nombre del Plan</label>
                <input type="text" class="form-input" v-model="form.nombre" required placeholder="Ej: Plan Básico, Profesional, Empresarial">
              </div>
              <div class="form-group">
                <label class="form-label">Descripción</label>
                <textarea class="form-textarea" v-model="form.descripcion" rows="3" placeholder="Describe las ventajas y características principales del plan"></textarea>
              </div>
              <div class="form-group">
                <label class="form-label">Precio</label>
                <div class="input-with-prefix">
                  <span class="input-prefix">$</span>
                  <input type="number" class="form-input" v-model="form.precio" step="0.01" required placeholder="0.00">
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Ciclo de Facturación</label>
                <select class="form-select" v-model="form.ciclo" required>
                  <option value="mensual">Mensual</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="anual">Anual</option>
                </select>
              </div>
            </div>
            <div class="form-section">
              <div class="form-group">
                <label class="form-label">Máximo de Usuarios</label>
                <input type="number" class="form-input" v-model="form.max_usuarios" required min="1" placeholder="Número de usuarios permitidos">
              </div>
              <div class="form-group">
                <label class="form-label">Facturas Anuales</label>
                <div class="facturas-input-group">
                  <div class="checkbox-group">
                    <input class="checkbox-input" type="checkbox" v-model="form.facturas_ilimitadas" id="facturas_ilimitadas">
                    <label class="checkbox-label-inline" for="facturas_ilimitadas">
                      <i class="fas fa-infinity"></i>
                      Facturas Ilimitadas
                    </label>
                  </div>
                  <input 
                    type="number" 
                    class="form-input" 
                    v-model="form.max_facturas_ano" 
                    :disabled="form.facturas_ilimitadas"
                    required 
                    min="1" 
                    placeholder="Número de facturas por año"
                  >
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Estado del Plan</label>
                <div class="checkbox-group">
                  <input class="checkbox-input" type="checkbox" v-model="form.activo" id="plan_activo">
                  <label class="checkbox-label-inline" for="plan_activo">
                    <i class="fas fa-toggle-on"></i>
                    Plan Activo
                  </label>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Plan Destacado</label>
                <div class="checkbox-group">
                  <input class="checkbox-input" type="checkbox" v-model="form.destacado" id="plan_destacado">
                  <label class="checkbox-label-inline" for="plan_destacado">
                    <i class="fas fa-star"></i>
                    Marcar como "Más Popular"
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <div class="features-section">
            <h4 class="section-title">
              <i class="fas fa-magic"></i>
              Características del Plan
            </h4>
            <div class="features-grid">
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.soporte_prioritario" id="soporte">
                <label class="checkbox-label" for="soporte">
                  <i class="fas fa-headset"></i>
                  Soporte Prioritario
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.soporte_telefonico" id="soporte_tel">
                <label class="checkbox-label" for="soporte_tel">
                  <i class="fas fa-phone"></i>
                  Soporte Telefónico
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.api_access" id="api">
                <label class="checkbox-label" for="api">
                  <i class="fas fa-code"></i>
                  Acceso API
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.reportes_avanzados" id="reportes">
                <label class="checkbox-label" for="reportes">
                  <i class="fas fa-chart-bar"></i>
                  Reportes Avanzados
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.exportacion_datos" id="exportacion">
                <label class="checkbox-label" for="exportacion">
                  <i class="fas fa-download"></i>
                  Exportación de Datos
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.backup_automatico" id="backup">
                <label class="checkbox-label" for="backup">
                  <i class="fas fa-shield-alt"></i>
                  Backup Automático
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.facturacion_electronica" id="fel">
                <label class="checkbox-label" for="fel">
                  <i class="fas fa-file-invoice"></i>
                  Facturación Electrónica
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.integraciones" id="integraciones">
                <label class="checkbox-label" for="integraciones">
                  <i class="fas fa-plug"></i>
                  Integraciones Externas
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.usuarios_ilimitados" id="usuarios_ilim">
                <label class="checkbox-label" for="usuarios_ilim">
                  <i class="fas fa-users"></i>
                  Usuarios Ilimitados
                </label>
              </div>
              <div class="checkbox-group">
                <input class="checkbox-input" type="checkbox" v-model="form.personalizacion_marca" id="branding">
                <label class="checkbox-label" for="branding">
                  <i class="fas fa-palette"></i>
                  Personalización de Marca
                </label>
              </div>
            </div>
          </div>

          <div class="modules-section">
            <h4 class="section-title">
              <i class="fas fa-cubes"></i>
              Módulos del Sistema Incluidos
            </h4>
            <p class="section-description">
              Selecciona qué módulos del sistema estarán disponibles para las empresas que contraten este plan
            </p>
            <div class="modules-grid" v-if="modulosDisponibles && modulosDisponibles.length > 0">
              <div class="checkbox-group" v-for="modulo in modulosDisponibles" :key="modulo.id">
                <input 
                  class="checkbox-input" 
                  type="checkbox" 
                  :id="`modulo_${modulo.id}`"
                  v-model="form.modulos_incluidos"
                  :value="modulo.id"
                >
                <label class="checkbox-label" :for="`modulo_${modulo.id}`">
                  <i class="fas fa-puzzle-piece"></i>
                  <div class="module-info">
                    <span class="module-name">{{ modulo.nombre }}</span>
                    <span class="module-description" v-if="modulo.descripcion">{{ modulo.descripcion }}</span>
                  </div>
                </label>
              </div>
            </div>
            <div v-else class="no-modules-message">
              <i class="fas fa-info-circle"></i>
              <p>No hay módulos disponibles. Debe configurar primero los módulos del sistema.</p>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn-secondary-custom" @click="closeModal">
          <i class="fas fa-times me-1"></i>
          Cancelar
        </button>
        <button type="button" class="btn-secondary-custom" @click="checkSelectedModules" style="background: var(--warning-color);">
          <i class="fas fa-bug me-1"></i>
          Debug Módulos
        </button>
        <button type="button" class="btn-primary-custom" @click="savePlan">
          <i :class="['fas', 'me-1', editingPlan ? 'fa-save' : 'fa-plus']"></i>
          {{ editingPlan ? 'Actualizar' : 'Crear' }} Plan
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import axios from 'axios'

export default {
  name: 'PlanesList',
  setup() {
    const planes = ref([])
    const modulosDisponibles = ref([])
    const showCreateModal = ref(false)
    const editingPlan = ref(null)
    
    const form = ref({
      nombre: '',
      descripcion: '',
      precio: 0,
      ciclo: 'mensual',
      max_usuarios: 1,
      max_facturas_ano: 1200,
      facturas_ilimitadas: false,
      activo: true,
      destacado: false,
      // Características del plan
      soporte_prioritario: false,
      soporte_telefonico: false,
      api_access: false,
      reportes_avanzados: false,
      exportacion_datos: false,
      backup_automatico: false,
      facturacion_electronica: false,
      integraciones: false,
      usuarios_ilimitados: false,
      personalizacion_marca: false,
      // Módulos incluidos
      modulos_incluidos: []
    })

    const loadPlanes = async () => {
      try {
        const response = await axios.get('/api/admin/planes')
        planes.value = response.data
      } catch (error) {
        console.error('Error loading planes:', error)
      }
    }

    const loadModulos = async () => {
      try {
        const response = await axios.get('/api/admin/modulos')
        modulosDisponibles.value = response.data
        console.log('Módulos cargados:', modulosDisponibles.value)
      } catch (error) {
        console.error('Error loading módulos:', error)
        // Datos de prueba mientras se configura la API
        modulosDisponibles.value = [
          { id: 1, nombre: 'Facturación', descripcion: 'Módulo de gestión de facturas' },
          { id: 2, nombre: 'Inventarios', descripcion: 'Control de productos y stock' },
          { id: 3, nombre: 'Clientes', descripcion: 'Gestión de clientes y contactos' },
          { id: 4, nombre: 'Reportes', descripcion: 'Reportes y estadísticas' },
          { id: 5, nombre: 'Contabilidad', descripcion: 'Módulo contable' },
          { id: 6, nombre: 'Ventas', descripcion: 'Gestión de ventas y cotizaciones' }
        ]
      }
    }

    const savePlan = async () => {
      try {
        console.log('Guardando plan con módulos:', form.value.modulos_incluidos)
        console.log('Datos completos del plan:', form.value)
        
        if (editingPlan.value) {
          await axios.put(`/api/admin/planes/${editingPlan.value.id}`, form.value)
        } else {
          await axios.post('/api/admin/planes', form.value)
        }
        loadPlanes()
        closeModal()
      } catch (error) {
        console.error('Error saving plan:', error)
        alert('Error al guardar el plan. Verifique los datos e intente nuevamente.')
      }
    }

    const editarPlan = (plan) => {
      editingPlan.value = plan
      form.value = { ...plan }
      // Convertir array de módulos a array de IDs si es necesario
      if (plan.modulos && Array.isArray(plan.modulos)) {
        form.value.modulos_incluidos = plan.modulos.map(m => m.id)
      }
      showCreateModal.value = true
    }

    const eliminarPlan = async (id) => {
      const result = await window.Swal.fire({
        title: 'Eliminar Plan',
        text: '¿Está seguro de eliminar este plan?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) return
      
      try {
        await axios.delete(`/api/admin/planes/${id}`)
        loadPlanes()
      } catch (error) {
        console.error('Error deleting plan:', error)
      }
    }

    const togglePlanActivo = async (plan) => {
      try {
        await axios.patch(`/api/admin/planes/${plan.id}/toggle-activo`, {
          activo: plan.activo
        })
      } catch (error) {
        console.error('Error toggling plan activo:', error)
        // Revertir el cambio en caso de error
        plan.activo = !plan.activo
      }
    }

    const closeModal = () => {
      showCreateModal.value = false
      editingPlan.value = null
      form.value = {
        nombre: '',
        descripcion: '',
        precio: 0,
        ciclo: 'mensual',
        max_usuarios: 1,
        max_facturas_ano: 1200,
        facturas_ilimitadas: false,
        activo: true,
        destacado: false,
        // Características del plan
        soporte_prioritario: false,
        soporte_telefonico: false,
        api_access: false,
        reportes_avanzados: false,
        exportacion_datos: false,
        backup_automatico: false,
        facturacion_electronica: false,
        integraciones: false,
        usuarios_ilimitados: false,
        personalizacion_marca: false,
        // Módulos incluidos
        modulos_incluidos: []
      }
    }

    const getPlanHeaderClass = (nombre) => {
      const classes = {
        basico: 'bg-secondary',
        profesional: 'bg-info',
        empresarial: 'bg-warning',
        premium: 'bg-success'
      }
      return classes[nombre?.toLowerCase()] || 'bg-primary'
    }

    // Función para verificar módulos seleccionados
    const checkSelectedModules = () => {
      console.log('Módulos seleccionados actualmente:', form.value.modulos_incluidos)
      console.log('Módulos disponibles:', modulosDisponibles.value)
    }

    onMounted(() => {
      loadPlanes()
      loadModulos()
    })

    return {
      planes,
      modulosDisponibles,
      showCreateModal,
      editingPlan,
      form,
      savePlan,
      editarPlan,
      eliminarPlan,
      togglePlanActivo,
      closeModal,
      getPlanHeaderClass,
      checkSelectedModules
    }
  }
}
</script>

<style>
/* Variables CSS para el tema */
:root {
  --primary-color: #3498db;
  --success-color: #27ae60;
  --warning-color: #f39c12;
  --danger-color: #e74c3c;
  --info-color: #17a2b8;
  --secondary-color: #6c757d;
}

/* Contenedor principal */
.planes-container {
  background-color: var(--bg-main);
  color: var(--text-primary);
  min-height: 100vh;
  padding: 1.5rem;
}

/* Header de la página */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0;
  display: flex;
  align-items: center;
}

/* Wrapper de contenido */
.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Grid de planes */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  align-items: stretch;
}

/* Tarjeta de plan */
.plan-card {
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px var(--shadow-color);
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 650px;
}

.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px var(--shadow-color);
}

.plan-highlighted {
  border-color: var(--warning-color);
  box-shadow: 0 4px 20px rgba(243, 156, 18, 0.2);
}

.plan-highlighted:hover {
  box-shadow: 0 8px 30px rgba(243, 156, 18, 0.3);
}

/* Header del plan */
.plan-header {
  padding: 2rem 1.5rem 1rem 1.5rem;
  text-align: center;
  position: relative;
  background: linear-gradient(135deg, var(--primary-color), #2980b9);
  color: white;
}

.plan-header.bg-secondary {
  background: linear-gradient(135deg, var(--secondary-color), #5a6268);
}

.plan-header.bg-info {
  background: linear-gradient(135deg, var(--info-color), #1f8dd6);
}

.plan-header.bg-warning {
  background: linear-gradient(135deg, var(--warning-color), #e67e22);
}

.plan-header.bg-success {
  background: linear-gradient(135deg, var(--success-color), #2ecc71);
}

.plan-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.popular-badge {
  position: absolute;
  top: -10px;
  right: 15px;
  background: var(--warning-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Cuerpo del plan */
.plan-body {
  padding: 2rem 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
}

.plan-price {
  margin-bottom: 2rem;
}

.price-amount {
  font-size: 3rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1;
}

.price-cycle {
  font-size: 1rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

/* Características del plan */
.plan-features {
  margin-bottom: 2rem;
  text-align: left;
  flex: 1;
  min-height: 150px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
}

.feature-item:last-child {
  border-bottom: none;
}

.feature-check {
  color: var(--success-color);
  font-size: 1rem;
  flex-shrink: 0;
}

.feature-item span {
  color: var(--text-primary);
  font-size: 0.95rem;
}

/* Módulos del plan */
.plan-modules {
  margin-bottom: 2rem;
  flex: 1;
  min-height: 120px;
}

.modules-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 0.5rem;
}

.modules-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: 100px;
  overflow-y: auto;
}

.module-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(52, 152, 219, 0.1);
  border: 1px solid rgba(52, 152, 219, 0.2);
  border-radius: 6px;
  color: var(--primary-color);
  font-size: 0.85rem;
  font-weight: 500;
}

.module-item i {
  font-size: 0.8rem;
}

/* Footer del plan - sección fija en la parte inferior */
.plan-footer {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
}

/* Estadísticas del plan */
.plan-stats {
  background: var(--secondary-bg-main);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

/* Acciones del plan */
.plan-actions {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.action-btn {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
}

.edit-btn {
  background: rgba(52, 152, 219, 0.1);
  color: var(--primary-color);
  border: 1px solid rgba(52, 152, 219, 0.2);
}

.edit-btn:hover {
  background: var(--primary-color);
  color: white;
  transform: translateY(-1px);
}

.delete-btn {
  background: rgba(231, 76, 60, 0.1);
  color: var(--danger-color);
  border: 1px solid rgba(231, 76, 60, 0.2);
}

.delete-btn:hover:not(:disabled) {
  background: var(--danger-color);
  color: white;
  transform: translateY(-1px);
}

.delete-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Toggle switch */
.plan-toggle {
  display: flex;
  justify-content: center;
}

.toggle-switch {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toggle-input {
  display: none;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.toggle-slider {
  width: 50px;
  height: 24px;
  background: var(--border-color);
  border-radius: 12px;
  position: relative;
  transition: all 0.3s ease;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-input:checked + .toggle-label .toggle-slider {
  background: var(--success-color);
}

.toggle-input:checked + .toggle-label .toggle-slider::before {
  transform: translateX(26px);
}

.toggle-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
}

/* Botones personalizados */
.btn-primary-custom,
.btn-secondary-custom {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
}

.btn-primary-custom {
  background: linear-gradient(135deg, var(--primary-color), #2980b9);
  color: white;
}

.btn-primary-custom:hover {
  background: linear-gradient(135deg, #2980b9, var(--primary-color));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.btn-secondary-custom {
  background: var(--secondary-color);
  color: white;
}

.btn-secondary-custom:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 2rem;
}

.modal-container {
  background: var(--card-bg);
  border-radius: 16px;
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: var(--danger-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: #c0392b;
  transform: scale(1.1);
}

.modal-body {
  padding: 2rem;
}

/* Formulario */
.plan-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
}

.form-input,
.form-textarea,
.form-select {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.input-with-prefix {
  display: flex;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  background: var(--input-bg);
}

.input-prefix {
  padding: 0.75rem;
  background: var(--secondary-bg-main);
  color: var(--text-secondary);
  font-weight: 600;
  border-right: 1px solid var(--border-color);
}

.input-with-prefix .form-input {
  border: none;
  border-radius: 0;
  flex: 1;
}

/* Grupo de entrada de facturas */
.facturas-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checkbox-label-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--card-bg);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  font-weight: 500;
}

.checkbox-label-inline:hover {
  background: var(--hover-bg);
  border-color: var(--primary-color);
}

.checkbox-input:checked + .checkbox-label-inline {
  background: rgba(52, 152, 219, 0.1);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.form-input:disabled {
  background: var(--secondary-bg-main);
  color: var(--text-secondary);
  cursor: not-allowed;
  opacity: 0.7;
}

/* Secciones especiales */
.features-section,
.modules-section {
  background: var(--secondary-bg-main);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid var(--border-color);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-description {
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.features-grid,
.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

/* Checkboxes personalizados */
.checkbox-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.checkbox-input {
  display: none;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.checkbox-label:hover {
  background: var(--hover-bg);
  border-color: var(--primary-color);
}

.checkbox-input:checked + .checkbox-label {
  background: rgba(52, 152, 219, 0.1);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.checkbox-label i {
  font-size: 1rem;
  flex-shrink: 0;
}

.module-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.module-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.module-description {
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.3;
}

.no-modules-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
  color: var(--text-secondary);
  background: var(--card-bg);
  border: 2px dashed var(--border-color);
  border-radius: 8px;
}

.no-modules-message i {
  font-size: 2rem;
  color: var(--warning-color);
}

.no-modules-message p {
  margin: 0;
  font-size: 0.9rem;
}

/* Footer del modal */
.modal-footer {
  padding: 1rem 2rem 2rem 2rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-top: 1px solid var(--border-color);
}

/* Responsive */
@media (max-width: 1200px) {
  .plans-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
  }
  
  .plan-card {
    min-height: 600px;
  }
}

@media (max-width: 768px) {
  .planes-container {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .plans-grid {
    grid-template-columns: 1fr;
  }
  
  .plan-card {
    min-height: auto;
  }
  
  .plan-actions {
    flex-direction: column;
  }
  
  .modal-overlay {
    padding: 1rem;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .features-grid,
  .modules-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-footer {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.5rem;
  }
  
  .price-amount {
    font-size: 2.5rem;
  }
  
  .plan-card {
    margin-bottom: 1rem;
  }
}
</style>
